import { useMemo, useState } from "react";
import { Box, Button, Flex, HStack, Menu, MenuButton, MenuItem, MenuList, Tag, Text } from "@chakra-ui/react";
import { CaretDown } from "@phosphor-icons/react";
import { ResponsiveBar } from "@nivo/bar";
import { ResponsiveLine } from "@nivo/line";
import PillToggle from "pages/Reports/Insights/components/PillToggle";
import { axisBaselineLayer, chartTheme } from "pages/Reports/Insights/components/Consumption/chartTheme";
import AdminOnlyValue from "./AdminOnlyValue";
import { getAllowancePace, RISK_COLORS } from "./allowancePace";
import { getMonthPeriodByOffset } from "./allowancePeriods";
import { sliceLastNMonths } from "./dailyUsage";
import { formatGbp } from "./formatGbp";

// Colors match the Insights dashboard's chart conventions (see
// pages/Reports/Insights/components/KpiCharts.jsx SCOPE_COLORS, EmissionsTrends.jsx /
// WaterfallChart.jsx axis chrome) — never the lime/limeShade brand tokens, which are
// reserved for UI chrome (filters, toggles), not data marks.
const AXIS_TICK_COLOR = "#718096";
const AXIS_LEGEND_COLOR = "#2D3748";
const GRID_COLOR = "#E2E8F0";
const MARKER_COLOR = "#2D3748";
// EmissionsTrends.jsx's "Total (S1+S2+S3)" line color — reused here for the daily total line.
const TOTAL_LINE_COLOR = "#333333";

const ALLOWANCE_LINE = 100;

const VIEW_OPTIONS = [
  { label: "Last 3 Months", value: "last3", months: 3 },
  { label: "Last 6 Months", value: "last6", months: 6 },
  { label: "Last 12 Months", value: "last12", months: 12 },
  { label: "This Year", value: "year", months: null },
];
const VIEW_YEAR = "year";

const CHART_BAR = "bar";
const CHART_LINE = "line";

const RISK_LABELS = {
  green: "On pace",
  amber: "Trending over",
  red: "Over pace",
};

/**
 * Formats a "YYYY-MM-01"-style ISO date string as "Jan 2026".
 */
function formatMonthLabel(isoDate) {
  const date = new Date(isoDate);
  return date.toLocaleDateString("en-GB", { month: "short", year: "numeric" });
}

/**
 * How many whole calendar months ago a "YYYY-MM-01" date is, relative to now.
 * 0 = current month, 1 = last month, negative = a future month (not yet happened).
 */
function monthsAgoFor(isoDate, now = new Date()) {
  const date = new Date(isoDate);
  return (now.getFullYear() - date.getFullYear()) * 12 + (now.getMonth() - date.getMonth());
}

function MockDataBadge() {
  return (
    <Tag colorScheme="orange" size="sm" fontWeight="600">
      Illustrative data
    </Tag>
  );
}

export default function MonthlyUsageChart({
  data,
  yearData,
  dailyDataYear,
  dailyDataLast12,
  annualLimitGbp,
  isMock,
  showSpend,
}) {
  const [view, setView] = useState("last12");
  const [chartType, setChartType] = useState(CHART_BAR);
  const isLineView = chartType === CHART_LINE;
  const isYearView = view === VIEW_YEAR;
  const activeViewOption = VIEW_OPTIONS.find((o) => o.value === view) || VIEW_OPTIONS[2];
  // "This Year" uses the Jan-to-date monthly/daily arrays as-is; the fixed windows
  // (3/6/12 months) slice the trailing N months out of the already-fetched Last 12
  // Months data — no extra query needed since 12 months is the widest window fetched.
  const activeData = isYearView ? yearData : (data || []).slice(-activeViewOption.months);
  const activeDailyData = isYearView
    ? dailyDataYear
    : sliceLastNMonths(dailyDataLast12, activeViewOption.months);

  const hasLimit =
    (activeData || []).some((row) => row.pct_of_allowance !== null && row.pct_of_allowance !== undefined) ||
    !!annualLimitGbp;

  const viewDropdown = (
    <Menu>
      <MenuButton
        as={Button}
        size="sm"
        borderRadius="full"
        border="1px solid"
        borderColor="gray.300"
        bg="transparent"
        color="gray.600"
        fontWeight="normal"
        rightIcon={<CaretDown size={14} />}
        _hover={{ bg: "gray.50", borderColor: "gray.400" }}
      >
        {activeViewOption.label}
      </MenuButton>
      <MenuList borderRadius="lg" border="1px solid" borderColor="gray.200" boxShadow="md" py={1}>
        {VIEW_OPTIONS.map((option) => (
          <MenuItem
            key={option.value}
            onClick={() => setView(option.value)}
            fontWeight={view === option.value ? "bold" : "normal"}
          >
            {option.label}
          </MenuItem>
        ))}
      </MenuList>
    </Menu>
  );

  const chartTypeToggle = (
    <PillToggle
      options={[
        { label: "Bar", value: CHART_BAR },
        { label: "Line", value: CHART_LINE },
      ]}
      selected={chartType}
      setSelected={setChartType}
    />
  );

  // Cumulative daily total as a % of the annual allowance, one point per day — real
  // data, bucketed client-side from task/context rows (see dailyUsage.js). Follows the
  // same period dropdown as the bar view: "This Year" resets the cumulative total to
  // zero on Jan 1; the fixed windows (3/6/12 months) are rolling, so the cumulative
  // total carries over from before the window starts.
  const dailyLineData = useMemo(() => {
    if (!annualLimitGbp) return [];
    let cumulativeCost = 0;
    return (activeDailyData || []).map((day) => {
      cumulativeCost += day.cost;
      return {
        x: day.date,
        y: Math.round((cumulativeCost / annualLimitGbp) * 1000) / 10,
        cumulativeCostGbp: cumulativeCost,
      };
    });
  }, [activeDailyData, annualLimitGbp]);

  const maxLinePct = Math.max(ALLOWANCE_LINE, ...dailyLineData.map((d) => d.y), 0);

  if (!hasLimit) {
    return (
      <Flex direction="column" gap={3}>
        <Flex justify="space-between" align="flex-start" gap={4}>
          <Text fontSize="body.m" fontWeight="600" color="gray.700">
            Monthly AI usage vs allowance
          </Text>
          {isMock && <MockDataBadge />}
        </Flex>
        <Text fontSize="body.xs" color="gray.500">
          No annual AI usage limit is set for this organisation. Set one in Organisation Management to see monthly
          usage as a percentage of your allowance here.
        </Text>
      </Flex>
    );
  }

  // Pace risk (RAG) per bar: a fully-elapsed past month collapses to raw % used
  // (daysElapsed=1, daysToReset=0 → 100% of the "period" elapsed); the current month
  // uses its real days-elapsed/to-reset so an early-month spike reads as risk, not a
  // guaranteed miss. See allowancePace.js / AllowanceProgressChart.jsx (same formula).
  const chartData = (activeData || [])
    .filter((row) => row.pct_of_allowance !== null && row.pct_of_allowance !== undefined)
    .map((row) => {
      const pct = Math.round((row.pct_of_allowance || 0) * 10) / 10;
      const monthsAgo = monthsAgoFor(row.month);
      const period = monthsAgo === 0 ? getMonthPeriodByOffset(0) : { daysElapsed: 1, daysToReset: 0 };
      const { projectedPct, risk } = getAllowancePace(pct, period.daysElapsed, period.daysToReset);
      return {
        month: formatMonthLabel(row.month),
        monthFull: formatMonthLabel(row.month),
        pct,
        projectedPct,
        risk,
        spendGbp: row.spend_gbp,
      };
    });

  const maxPct = Math.max(ALLOWANCE_LINE, ...chartData.map((row) => row.pct));

  return (
    <Flex direction="column" gap={3}>
      <Flex justify="space-between" align="flex-start" gap={4}>
        <Box>
          <Text fontSize="body.m" fontWeight="600" color="gray.700">
            Monthly AI usage vs allowance
          </Text>
          <Text fontSize="body.xs" color="gray.500">
            {isLineView
              ? `Cumulative total usage ${isYearView ? "so far this year" : `over the ${activeViewOption.label.toLowerCase()}`}, as a percentage of your annual allowance, plotted day by day.`
              : "Usage each month as a percentage of your monthly allowance (annual limit ÷ 12). Bar color reflects pace risk, not just raw usage."}
          </Text>
        </Box>
        {isMock && <MockDataBadge />}
      </Flex>

      <Flex justify="space-between" align="center" flexWrap="wrap" gap={4}>
        <HStack gap={3}>
          {chartTypeToggle}
          {viewDropdown}
        </HStack>
        {!isLineView && (
          <HStack gap={4}>
            {Object.entries(RISK_LABELS).map(([risk, label]) => (
              <HStack key={risk} gap={1.5}>
                <Box w="8px" h="8px" borderRadius="full" bg={RISK_COLORS[risk]} />
                <Text fontSize="body.xs" color="gray.600">
                  {label}
                </Text>
              </HStack>
            ))}
          </HStack>
        )}
      </Flex>

      {isLineView ? (
        <Box bg="white" border="1px solid" borderColor="gray.200" borderRadius="md" p={4} h="380px">
          <ResponsiveLine
            data={[{ id: "Total usage", color: TOTAL_LINE_COLOR, data: dailyLineData }]}
            margin={{ top: 20, right: 24, bottom: 56, left: 64 }}
            xScale={{ type: "point" }}
            yScale={{ type: "linear", min: 0, max: maxLinePct * 1.05, stacked: false }}
            colors={({ color }) => color}
            curve="monotoneX"
            enableArea
            areaOpacity={0.12}
            // Custom lines + points layers (not Nivo's built-in animated points) —
            // mirrors EmissionsTrends.jsx / ScopeTwoDetail.jsx's 5-year trend chart so
            // this line follows the same visual convention as the Insights dashboards.
            enablePoints={false}
            layers={[
              "grid",
              "markers",
              "axes",
              axisBaselineLayer,
              "areas",
              "crosshair",
              ({ series, lineGenerator, xScale, yScale }) => (
                <g>
                  {series.map((s) => {
                    const path = lineGenerator(s.data.map((d) => ({ x: xScale(d.data.x), y: yScale(d.data.y) })));
                    return (
                      <path
                        key={s.id}
                        d={path}
                        fill="none"
                        stroke={s.color}
                        strokeWidth={2}
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    );
                  })}
                </g>
              ),
              ({ series, xScale, yScale }) => (
                <g>
                  {series.map((s) =>
                    s.data.map((d, i) => (
                      <circle
                        key={`${s.id}-${i}`}
                        cx={xScale(d.data.x)}
                        cy={yScale(d.data.y)}
                        r={3}
                        fill="#FFFFFF"
                        stroke={s.color}
                        strokeWidth={2}
                      />
                    ))
                  )}
                </g>
              ),
              "slices",
              "mesh",
              ({ yScale }) => (
                <text x={4} y={yScale(ALLOWANCE_LINE) - 6} fontSize={11} fill={MARKER_COLOR}>
                  Allowance
                </text>
              ),
            ]}
            axisBottom={{
              tickSize: 0,
              tickPadding: 6,
              tickValues: dailyLineData.filter((_, i) => i % 30 === 0).map((d) => d.x),
              format: (v) => new Date(v).toLocaleDateString("en-GB", { month: "short", year: "2-digit" }),
            }}
            axisLeft={{
              tickSize: 0,
              tickPadding: 6,
              format: (v) => `${v}%`,
            }}
            enableGridX={false}
            theme={chartTheme}
            markers={[
              {
                axis: "y",
                value: ALLOWANCE_LINE,
                lineStyle: { stroke: MARKER_COLOR, strokeWidth: 1.5, strokeDasharray: "4 4" },
              },
            ]}
            tooltip={({ point }) => (
              <Box bg="white" px={3} py={2} borderRadius="md" boxShadow="md" border="1px solid" borderColor="gray.200">
                <Text fontSize="body.s" fontWeight="700" color="gray.800">
                  {new Date(point.data.x).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}
                </Text>
                <Text fontSize="body.s" color={point.serieColor}>
                  {Math.round(point.data.y * 10) / 10}% of annual allowance used (cumulative)
                </Text>
                {showSpend && formatGbp(point.data.cumulativeCostGbp) && (
                  <AdminOnlyValue>{formatGbp(point.data.cumulativeCostGbp)}</AdminOnlyValue>
                )}
              </Box>
            )}
            useMesh
            role="application"
            ariaLabel="Cumulative daily AI usage as percentage of annual allowance"
          />
        </Box>
      ) : (
        <Box height="380px" width="100%">
          <ResponsiveBar
            data={chartData}
            keys={["pct"]}
            indexBy="month"
            layout="horizontal"
            margin={{ top: 30, right: 40, bottom: 40, left: 90 }}
            padding={0.35}
            valueScale={{ type: "linear", min: 0, max: maxPct * 1.05 }}
            indexScale={{ type: "band", round: true }}
            colors={(bar) => RISK_COLORS[bar.data.risk]}
            borderRadius={2}
            axisTop={null}
            axisRight={null}
            axisBottom={{
              tickSize: 0,
              tickPadding: 8,
              legend: "% of monthly allowance",
              legendPosition: "middle",
              legendOffset: 32,
            }}
            axisLeft={{
              tickSize: 0,
              tickPadding: 8,
            }}
            theme={{
              axis: {
                ticks: { text: { fill: AXIS_TICK_COLOR, fontSize: 11 } },
                legend: { text: { fill: AXIS_LEGEND_COLOR, fontSize: 12, fontWeight: 600 } },
              },
              grid: { line: { stroke: GRID_COLOR } },
            }}
            enableGridX={true}
            enableGridY={false}
            enableLabel={false}
            markers={[
              {
                axis: "x",
                value: ALLOWANCE_LINE,
                lineStyle: { stroke: MARKER_COLOR, strokeWidth: 1.5, strokeDasharray: "4 4" },
              },
            ]}
            layers={[
              "grid",
              "axes",
              "bars",
              "markers",
              "legends",
              ({ xScale }) => (
                <text x={xScale(ALLOWANCE_LINE)} y={-10} textAnchor="middle" fontSize={11} fill={MARKER_COLOR}>
                  Allowance
                </text>
              ),
              ({ bars }) =>
                bars
                  .filter((bar) => bar.data.data.pct > ALLOWANCE_LINE)
                  .map((bar) => (
                    <text
                      key={bar.key}
                      x={bar.x + bar.width + 8}
                      y={bar.y + bar.height / 2}
                      dominantBaseline="central"
                      fontSize={11}
                      fontWeight="600"
                      fill={RISK_COLORS[bar.data.data.risk]}
                    >
                      {Math.round(bar.data.data.pct)}%
                    </text>
                  )),
            ]}
            tooltip={({ data: rowData }) => (
              <Box bg="white" boxShadow="md" borderRadius="md" p={2}>
                <Text fontSize="body.s" fontWeight="600" color="gray.700">
                  {rowData.monthFull}
                </Text>
                <Text fontSize="body.s" color="gray.600">
                  {Math.round(rowData.pct)}% of monthly allowance used
                </Text>
                {showSpend && formatGbp(rowData.spendGbp) && (
                  <AdminOnlyValue>{formatGbp(rowData.spendGbp)}</AdminOnlyValue>
                )}
                <Text fontSize="body.s" color={RISK_COLORS[rowData.risk]} fontWeight="600">
                  {RISK_LABELS[rowData.risk]} — projected {Math.round(rowData.projectedPct)}%
                </Text>
              </Box>
            )}
            role="application"
            ariaLabel="Monthly AI usage as percentage of allowance"
          />
        </Box>
      )}
      <Text fontSize="body.xs" color="gray.500">
        {isLineView
          ? "The line tracks cumulative spend against your annual allowance day by day — a steepening slope means usage is accelerating."
          : "Bar color reflects pace risk — how fast that month's usage is running relative to how much of the month has elapsed, not just raw % used. Consistently amber/red months are worth reviewing before your annual limit runs out early."}
        {isMock && " This chart currently shows illustrative placeholder data — no real usage limit is configured yet."}
      </Text>
    </Flex>
  );
}
